const ATTACHMENT_FIELD_TYPES = new Set(['file', 'signaturepad']);
function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
/** SurveyJS file questions store { name, type, content } with base64 data URLs. */
export function isSurveyFileAttachmentValue(value) {
    if (typeof value === 'string') {
        return value.startsWith('data:') && value.length > 256;
    }
    if (!Array.isArray(value) || value.length === 0) {
        return false;
    }
    return value.every((item) => isPlainObject(item) &&
        typeof item.name === 'string' &&
        typeof item.content === 'string' &&
        (item.content.startsWith('data:') || typeof item.type === 'string'));
}
export function collectAttachmentFieldPaths(definition) {
    const paths = new Set();
    function walk(elements, panelPrefix) {
        for (const el of elements || []) {
            if (el.type === 'panel') {
                walk(el.elements, el.name || '');
            }
            else if (el.type === 'paneldynamic') {
                if (el.name)
                    paths.add(el.name);
                for (const templateEl of el.templateElements || []) {
                    if (templateEl.name && ATTACHMENT_FIELD_TYPES.has(templateEl.type || '')) {
                        paths.add(`${el.name}[0].${templateEl.name}`);
                        paths.add(templateEl.name);
                    }
                }
            }
            else if (el.name && ATTACHMENT_FIELD_TYPES.has(el.type || '')) {
                const path = panelPrefix ? `${panelPrefix}.${el.name}` : el.name;
                paths.add(path);
                paths.add(el.name);
            }
        }
    }
    for (const page of definition?.pages || []) {
        walk(page.elements, '');
    }
    if (definition?.elements) {
        walk(definition.elements, '');
    }
    return paths;
}
function pathMatchesAttachmentField(path, attachmentPaths) {
    if (attachmentPaths.has(path))
        return true;
    const fieldName = path.split('.').pop()?.replace(/\[\d+\]$/, '') || path;
    if (attachmentPaths.has(fieldName))
        return true;
    return [...attachmentPaths].some((attachmentPath) => path === attachmentPath ||
        path.endsWith(`.${attachmentPath}`) ||
        attachmentPath.endsWith(`.${fieldName}`));
}
function stripAttachmentValues(value, attachmentPaths, currentPath = '') {
    if (currentPath && pathMatchesAttachmentField(currentPath, attachmentPaths)) {
        return undefined;
    }
    if (isSurveyFileAttachmentValue(value)) {
        return undefined;
    }
    if (Array.isArray(value)) {
        const nextPath = currentPath ? `${currentPath}[]` : '';
        const items = value
            .map((item, index) => stripAttachmentValues(item, attachmentPaths, currentPath ? `${currentPath}[${index}]` : `[${index}]`))
            .filter((item) => item !== undefined);
        if (nextPath && pathMatchesAttachmentField(nextPath, attachmentPaths)) {
            return undefined;
        }
        return items;
    }
    if (isPlainObject(value)) {
        const result = {};
        for (const [key, child] of Object.entries(value)) {
            const path = currentPath ? `${currentPath}.${key}` : key;
            if (pathMatchesAttachmentField(path, attachmentPaths))
                continue;
            if (isSurveyFileAttachmentValue(child))
                continue;
            const stripped = stripAttachmentValues(child, attachmentPaths, path);
            if (stripped !== undefined) {
                result[key] = stripped;
            }
        }
        return result;
    }
    return value;
}
export function stripAttachmentData(data, attachmentPaths) {
    const stripped = stripAttachmentValues(data, attachmentPaths);
    return isPlainObject(stripped) ? stripped : {};
}
export function sanitizeWorkflowPayload(request) {
    const definition = request.formDefinition;
    const attachmentPaths = collectAttachmentFieldPaths(definition);
    return {
        ...request,
        currentData: stripAttachmentData(request.currentData ?? {}, attachmentPaths),
        conversationHistory: (request.conversationHistory ?? []).map((turn) => ({
            ...turn,
            surveyData: stripAttachmentData(turn.surveyData ?? {}, attachmentPaths),
        })),
    };
}
